/*
  Что происходило по счёту — лента событий (§3.3.5).

  🔴 Названа так 25.09.2026, вместе с переработкой шапки карточки. Прежнее имя
  ленты - «Как движется дело» - досталось колонке шапки, где дорога взыскания
  показывает, ГДЕ счёт стоит сейчас; лента отвечает на другой вопрос - что по
  нему уже было. Два одинаковых заголовка на одном экране человек читает как
  одно и то же, и искать нужное начинает наугад.

  Заведена 21.09.2026 по указанию владельца. До неё карточка показывала историю
  смен стадий и список документов, а всё остальное — звонки, обещания,
  поступления, судьбу приказа, страницу должника — приходилось собирать глазами
  из пяти разных разделов кабинета. Вопрос «что по этому счёту вообще делали»
  ответа на одном экране не имел, хотя конституция описывает карточку именно
  так с первого дня.

  Блок «История» отсюда исчез не случайно: смены стадий стали частью ленты.
  Двух хронологий на одной карточке быть не должно — человек читал бы их как
  разные события.

  Три решения, за которыми стоят правила кабинета:

  1. **Текст события приходит готовым с сервера.** Иначе кабинету пришлось бы
     держать семь справочников подписей (исходы звонков, виды документов,
     события приказа, источники обещаний...), и каждое новое значение на
     сервере означало бы пустую строку в ленте до следующей правки кабинета.
  2. **Сумма приходит числом и форматируется здесь** — тем же `money`, что и
     остальные суммы карточки (§4.5.1).
  3. **Время показывается только там, где оно настоящее.** У половины событий
     источник хранит один день: удержание по приказу, поступление по выписке,
     срок обещания. Полночь на экране выглядела бы как знание, которого нет.
*/

import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client.js'
import { date, dateTime, money } from '../api/format.js'
import { Empty, Loading, Notice, Tag } from './Common.jsx'
import PageBar from './PageBar.jsx'
import usePagedList from './usePagedList.js'

/*
  Сколько событий показывать сразу. По счёту, который ведут полгода, их
  набирается не одна сотня: каждый звонок волны, каждое сообщение, каждая
  сборка пакета. Смотрят при этом последние — то, на чём дело стоит сейчас.

  21.10.2026: самодельное «Показать ещё 20» заменено общей панелью страниц, а страницы
  режет сервер (`usePagedList`): размер выбирается и запоминается один на все списки.
*/

/*
  Длиннее этого примечание сворачивается. Заведено 23.09.2026 вместе с записью
  разговоров: расшифровка звонка - это две-три тысячи знаков, и развёрнутая она
  вытесняет из ленты все остальные события. Короткое примечание (длительность
  звонка, причина смены стадии) прятать не за чем - оно читается сразу.

  🔴 25.09.2026 расшифровка из ленты ушла совсем (решение владельца): сам
  разговор читается на странице обращения, куда ведёт строка. Сворачивание
  осталось для длинных примечаний других событий - названий собранных
  документов, причины смены стадии.
*/
const NOTE_SHORT = 180

/*
  Цветом помечены только деньги: поступление — единственное событие ленты,
  которое человек ищет глазами, пробегая её сверху вниз. Красный в кабинете
  означает потерю денег во времени и в ленте не используется вовсе (§4.5.3):
  горящая давность видна плиткой выше, а событие прошлого гореть не может.
*/
function toneOf(group) {
  return group === 'money' ? 'calm' : 'muted'
}

/*
  Примечание события. Расшифровка разговора приходит репликами через перевод
  строки, и показывать её сплошной строкой нельзя: «Агент: ... Должник: ...» в
  один абзац не читается вовсе (осмотр 23.09.2026, живой разговор из шести
  реплик). Отсюда `note--lines` с переносами и сворачивание длинного.
*/
function Note({ text }) {
  const [open, setOpen] = useState(false)
  const long = text.length > NOTE_SHORT
  if (!long) return <div className="muted note--lines">{text}</div>
  return (
    <>
      <div className="muted note--lines">
        {open ? text : `${text.slice(0, NOTE_SHORT).trimEnd()}…`}
      </div>
      {/* Кнопка отдельной строкой, а не в потоке текста: в потоке она разрывает
          последнюю строку примечания пополам (осмотр 23.09.2026). */}
      <button type="button" className="button button--quiet" onClick={() => setOpen(!open)}>
        {open ? 'Свернуть' : 'Показать целиком'}
      </button>
    </>
  )
}

export default function AccountTimeline({ accountId }) {
  const load = useCallback((window) => api.accountTimeline(accountId, window), [accountId])
  // Страницы режет сервер (21.10.2026): он собирает ленту из нескольких источников, режет
  // её по окну и называет общее число; в браузер уходит одна страница.
  const { rows: pageEvents, error, bar } = usePagedList(load)

  return (
    <section className="section">
      <h2 className="section__title">Что происходило по счёту</h2>
      <Notice error={error} />

      {error ? null : pageEvents === null ? (
        <Loading what="Собираем историю счёта" />
      ) : pageEvents.length === 0 ? (
        <Empty title="По счёту ещё ничего не делали">
          Здесь появится всё, что происходит с делом: звонки и сообщения должнику, его обещания и
          платежи, собранные документы, подача в суд и судьба приказа после неё.
        </Empty>
      ) : (
        <ul className="log card">
          {pageEvents.map((event, index) => (
            /* Своего идентификатора у события нет: лента складывается из шести
               таблиц, и общего ключа у них не может быть. Индекс здесь
               безопасен - лента только на чтение, из середины ничего не
               удаляется, список заменяется целиком (правило 4 скилла). */
            <li className="log__row" key={`${event.happened_on}-${event.group}-${index}`}>
              <span className="log__when">
                {event.occurred_at ? dateTime(event.occurred_at) : date(event.happened_on)}
              </span>
              <Tag kind={toneOf(event.group)}>{event.group_title}</Tag>
              <span>
                {/* Обращение открывается своей страницей: там разговор
                    репликами, путь по шагам и судьба выданной ссылки. В самой
                    ленте разговора нет - решение владельца 25.09.2026. */}
                {event.communication_id ? (
                  <Link to={`/communications/${event.communication_id}`}>
                    <strong>{event.title}</strong>
                  </Link>
                ) : (
                  <strong>{event.title}</strong>
                )}
                {/* Пробел между заголовком и суммой стоит текстом, а не только
                    отступом: отступ видит глаз, но не читалка экрана - она
                    прочитала бы «обещал оплатить4 000 ₽» одним словом
                    (осмотр 21.09.2026). */}
                {event.amount === null ? null : (
                  <>
                    {' '}
                    <span className="log__amount">{money(event.amount)}</span>
                  </>
                )}
                {event.note ? <Note text={event.note} /> : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      <PageBar {...bar} what="событий" />
    </section>
  )
}
